import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, MapPin, ArrowRight } from "lucide-react";
import { getVenueDirectory } from "@/lib/api";

/**
 * LiveNowStrip — a tasteful ticker that renders ONLY when at least one venue
 * has active check-ins right now. Silently absent otherwise (honest — no
 * fabrication of a "12 venues live" line when nothing is happening).
 */
export default function LiveNowStrip() {
  const [venues, setVenues] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = () => {
      getVenueDirectory()
        .then((list) => {
          if (cancel) return;
          const live = (list || [])
            .filter((v) => v.live_now > 0)
            .sort((a, b) => b.live_now - a.live_now)
            .slice(0, 3);
          setVenues(live);
          setLoaded(true);
        })
        .catch(() => setLoaded(true));
    };
    load();
    // refresh every 45 seconds so it feels alive
    const t = setInterval(load, 45000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  if (!loaded || venues.length === 0) return null;

  return (
    <section className="relative z-10 pt-2 pb-6 md:pt-4 md:pb-10" data-testid="live-now-strip">
      <div className="container-x">
        <div className="glass rounded-2xl overflow-hidden ring-1 ring-neon-cyan/25">
          <div className="flex flex-col md:flex-row md:items-stretch">
            {/* Left label */}
            <div className="md:w-52 shrink-0 flex items-center gap-2 px-5 py-4 md:py-0 bg-gradient-to-r from-neon-cyan/15 to-neon-cyan/0 border-b md:border-b-0 md:border-r border-white/5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-neon-cyan opacity-70 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-neon-cyan" />
              </span>
              <span className="text-xs uppercase tracking-widest text-neon-cyan font-semibold flex items-center gap-1.5">
                <Radio className="w-3.5 h-3.5" /> Live now
              </span>
            </div>

            {/* Venue rows */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
              <AnimatePresence initial={false}>
                {venues.map((v, i) => (
                  <motion.div
                    key={v.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.05 }}
                    className="px-5 py-4 flex items-center gap-3"
                    data-testid={`live-strip-venue-${v.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{v.name}</p>
                      <p className="text-[11px] text-zinc-500 flex items-center gap-1 truncate">
                        {v.neighborhood && (<><MapPin className="w-2.5 h-2.5" />{v.neighborhood}</>)}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-neon-cyan/15 text-neon-cyan px-2.5 py-1 text-xs font-bold tabular-nums">
                      {v.live_now} here
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* CTA */}
            <Link
              to="/venues/directory"
              data-testid="live-strip-see-all"
              className="hidden md:flex items-center gap-1.5 px-5 py-4 border-l border-white/5 text-sm text-neon-cyan font-semibold hover:text-white transition-colors"
            >
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
